import dayjs from 'dayjs';
import { getNested, isVisible, parseFetchResponse } from '../../../node_modules/glo-shared-js/utils/helpers';

import {
	identify,
	track,
	trackAdjustEvent,
	trackSuccessfulRegistration,
	trackSuccessfulTrialSignup,
	createTraitsPlaceholder,
	createUserTraits,
	createPersonalizationTraits,
	createAdTraits,
} from '../../../node_modules/glo-shared-js/utils/analytics';

import {
	addError,
	checkIfFormComplete,
	disableForm,
	enableForm,
	formatErrors,
	gloValidate,
	hasError,
	promoCodeErrors,
	removeError,
	toggleSubmitButton,
	userInfo,
	validatePromoCode
} from '../../../node_modules/glo-shared-js/utils/validation';

import {
	applePayRequest,
	cardElement,
	initPayment,
	loadSubscriptionPlans,
	paymentProcessingText,
	processPayment,
	submitButtonText
} from '../../../node_modules/glo-shared-js/utils/payment';

import { resetAllSelections } from './utils/onboarding-selections';
import { getAdParams } from './utils/ad-params';

var applePaymentTokenIsHandled = false;

document.addEventListener('DOMContentLoaded', () => {
	// If form has loaded, mount the Stripe elements
	if (document.getElementById('register-form__card')) initPayment();

	if (document.querySelector('[name="subscription-plans-data"]')) loadSubscriptionPlans();

	/**************************
	 * Apple Pay/Stripe Setup *
	 **************************/

	// Check if Apple Pay is supported
	applePayRequest.canMakePayment().then(function(paymentTypes) {
		if (paymentTypes && paymentTypes.applePay && document.querySelector('.apple-pay-container')) {
			document.querySelector('.apple-pay-container').style.display = 'block';
			document.querySelector('.form__button-container').classList.add('form__button-container--has-apple-pay');
		}
	});

	var buildApplePaySession = function(callback, onRegisterForm) {
		var formId = onRegisterForm ? '.register-form' : '#code-redemption-payment-form';
		var applePayError = 'There was an error processing your Apple Pay. Please try again or enter in credit card details.';

		if (!applePaymentTokenIsHandled) {
			applePayRequest.on('token', function(result) {
				if (getNested(result, 'token', 'id') && result.token.id) {
					callback(result.token.id, result.complete);
				} else {
					addError('apple-pay', applePayError);
					enableForm(formId);
					toggleSubmitButton(false, (onRegisterForm ? submitButtonText : 'Submit'));
					result.complete('fail');
				}
			});
			applePaymentTokenIsHandled = true;
		}
	};

	/*******************
	 * Form Validation *
	 *******************/

	document.querySelectorAll('.form__input--validate-keyup').forEach(function(element) {
		element.addEventListener('keyup', function(e) {
			var field = e.target.getAttribute('name');

			// Only clear the current error if user didn't just "tab" into field or click the "Enter" key
			if (hasError(field) && e.which !== 9 && e.which !== 13 && isVisible(e.target)) {
				removeError(field);
			}
		});

		element.addEventListener('blur', function(e) {
			var field = e.target.getAttribute('name');
			gloValidate[field]();

			if (field === 'email' && document.querySelector('input[type="password"]') && document.querySelector('input[type="password"]').value) {
				gloValidate.password();
			}
		});
	});

	if (document.getElementById('register-form__terms')) {
		document.getElementById('register-form__terms').addEventListener('click', function() {
			gloValidate.terms();
		});
	}

	// If an email or promo value exists when loading the page, validate it immediately
	if (document.getElementById('register-form__email') && document.getElementById('register-form__email').value) {
		gloValidate.email();
	}

	/*******************
	 * Form Submission *
	 *******************/

	var validationBeforeSubmit = function(formClassName) {
		// Remove any generic form errors that might exist
		document.querySelector('.form__generic-errors').classList.add('hide');
		document.querySelector('.form__generic-errors').innerHTML = '';

		// Disable the submit button to prevent repeated clicks
		toggleSubmitButton(true, paymentProcessingText);
		removeError('general');
		removeError('apple-pay');
		disableForm(formClassName, cardElement);
	};

	var onboardingFlow = document.querySelector('.onboarding-carousel');

	/*******************************
	 * Create User Form Submission *
	 *******************************/

	const createUserForm = document.getElementById('create-user-form');
	if (createUserForm) {
		createUserForm.addEventListener('submit', function(e) {
			e.preventDefault();

			var recaptchaLoaded = typeof window.grecaptcha === 'object';
			var recaptchaResponse = recaptchaLoaded
				? window.grecaptcha.getResponse()
				: null;
			var userTimezone = window.Intl
				? window.Intl.DateTimeFormat().resolvedOptions().timeZone
				: null;
			var optInMarketing = document.querySelector('.register-form__opt-in-marketing');
			var options;
			var data;

			userInfo.locale = navigator.language;
			userInfo.optInMarketing = true;
			userInfo.timezone = userTimezone;

			if (optInMarketing && document.querySelector('.register-form input[name="opt-in-marketing"]')) {
				userInfo.optInMarketing = document.querySelector('.register-form input[name="opt-in-marketing"]').checked;
			}

			if (!checkIfFormComplete('create-user')) return;

			// If the Google captcha element exists but doesn't have a valid value,
			// then hold off on submitting registration
			if (recaptchaLoaded && !recaptchaResponse) {
				grecaptcha.execute();
				return;
			}

			// Disable button while form is being submitted
			removeError('general');
			disableForm('#create-user-form');

			data = {
				userInfo,
				onboardingSelections: onboardingFlow ? window.onboardingSelections : null
			};

			if (recaptchaResponse) {
				data['g-recaptcha-response'] = recaptchaResponse;
			}

			if (typeof window.Adjust === 'object') {
				let trackingIdentifier;
				let trackingType;

				if (window.Adjust.getAttribution()) {
					trackingIdentifier = window.Adjust.getAttribution().adid;
					trackingType = 'adid';
				} else if ((window.Adjust.getWebUUID())) {
					trackingIdentifier = window.Adjust.getWebUUID();
					trackingType = 'web_uuid';
				}

				data['trackingIdentifier'] = trackingIdentifier;
				data['trackingType'] = trackingType;
			}

			options = {
				method: 'POST',
				headers: {
					'Content-type': 'application/json'
				},
				body: JSON.stringify(data),
			};

			const defaultErrorMessage = 'We encountered an issue. Try again, or contact support@glo.com.';
			fetch('/proxy/v1/createUser', options)
				.then(parseFetchResponse)
				.then((response) => {
					if (response.data) {
						var userId = response.data.createUser.id;
						var email = response.data.createUser.email;
						var trackProperties = response.utmParams || {};
						var optimizelyId = (onboardingFlow && window.analytics && typeof window.analytics.user === 'function')
							? window.analytics.user().anonymousId()
							: null;

						// Clear onboarding selections so page can be reloaded
						resetAllSelections();
						const wasReferredElement = document.getElementById('was-referred');

						trackProperties.user_id = userId;
						trackProperties.email = email;
						trackProperties.was_referred = wasReferredElement && wasReferredElement.value == 'true';

						if (window.analytics && typeof window.analytics.alias === 'function') {
							window.analytics.alias(userId);
						}

						// We need to identify the user here now that a known userId is defined.
						// At this point, the anonymousId is regenerated, hence needing to save
						// it as a variable above to be used in trackSuccessfulRegistration
						identify(userId, {
							...createTraitsPlaceholder(),
							...createUserTraits(response.data.createUser),
							...createAdTraits(getAdParams())
						});

						trackProperties = {
							...trackProperties,
						};

						const user = response.data.createUser;

						const traits = {
							email: user.email,
							firstName: user.firstName,
							lastName: user.lastName,
							phone: user.phoneNumber,
							city: user.city,
							address: {
								state: user.state,
								postalCode: user.zip,
								country: user.country
							},
							externalId: user.id
						};

						trackSuccessfulRegistration(optimizelyId, trackProperties, email, userId, null, {
							traits
						});

						// Track Meta Pixel event
						track('Web Account Registration - Successful', trackProperties, null, {
							traits
						});

						track('Signed Up', trackProperties, null, {
							traits
						});

						track('account_created', trackProperties, null, {
							traits
						});


						if (window.location.pathname === '/v2/register') {
							// A hack to wait for GTM to trigger Google Ads tag
							setTimeout(() => {
								window.location.href = '/v2/onboarding';
							}, 2000);
						} else {
							window.location.reload();
						}
					} else {
						const addCustomRegistrationFormErrors = (error) => {
							if (error.param === 'User already exists') {
								addError('email', 'A Glo account already exists with this email.');
							} else if (error.param === 'password') {
								addError('password', 'Must contain at least 8 characters.');
							} else if (error.msg === 'Rate limit exceeded') {
								addError(null, 'You’ve exceeded the number of registration attempts. Please try again in 24 hours.');
							} else {
								addError(error.param, defaultErrorMessage);
							}
						};

						if (response && typeof response === 'object') {
							if (Array.isArray(response)) {

								response.forEach(addCustomRegistrationFormErrors);
							} else {
								addCustomRegistrationFormErrors({
									param: getNested(response, 'error', 'param'),
									msg: getNested(response, 'error', 'msg')
								});
							}
						} else {
							addError(null, defaultErrorMessage);
						}

						toggleSubmitButton(false, 'Create Account');
						enableForm('#create-user-form');
						trackAdjustEvent('Account Registration - Failed');
					}
				})
				.catch(() => {
					addError(null, defaultErrorMessage);
					toggleSubmitButton(false, 'Create Account');
					enableForm('#create-user-form');
					trackAdjustEvent('Account Registration - Failed');
				});
		});
	}

	/************************************************
	 * Add Payment and Save Subscription Submission *
	 ************************************************/

	var addPaymentAndSaveSubscription = function(data, redirectUrl, gaTracking, applePayCompletion, errorCallback) {
		var options = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		};

		fetch('/proxy/v1/addPaymentAndSaveSubscription', options)
			.then(function(response) {
				if (response.ok) {
					response.json().then(function(responseJSON) {
						identify(responseJSON.trackingProperties.user_id, {
							...createPersonalizationTraits(responseJSON.trackingProperties)
						});
						var redirect = redirectUrl || responseJSON.redirect;
						var safetyTimeout = 8000;
						var optimizelyId = window.optimizelyAnonymousId
							|| (getNested(window, 'analytics', 'user') ? window.analytics.user().anonymousId() : null);
						var callback = function() {
							if (applePayCompletion) {
								applePayCompletion('success');
								// iOS has redirect issues when window.location is
								// processed too quickly after an Apple Pay session
								setTimeout(function() {
									window.location = redirect;
								}, 3000);
							} else {
								window.location = redirect;
							}
						};

						// Safety net timeout, in case analytics fails
						var timeoutID = setTimeout(callback, safetyTimeout, redirect);

						trackSuccessfulTrialSignup(optimizelyId, { ...responseJSON.trackingProperties  }, function() {
							const trackingProperties = {
								...gaTracking.properties,
								...responseJSON.trackingProperties
							};
							// Track Meta Pixel event
							track('Web Account Subscription - Trial Conversion Successful', trackingProperties);
							track('Trial Activated', trackingProperties);
							track('FB-Trial-Convert-Win', trackingProperties);
							track('account_trial_started', trackingProperties);
							track(gaTracking.event, trackingProperties, function() {
								clearTimeout(timeoutID);
								callback();
							});
							const wasReferred = document.getElementById('was-referred').value == 'true';
							if (wasReferred) {
								var pageName = window.location.pathname.split('/')[1];
								track('Referral - redeemed', { page_name: pageName });
							}
						});
					});
				} else {
					response.json().then(errorCallback);
				}
			})
			.catch(function() {
				errorCallback();
			});
	};

	/*****************************************
	 * Promo Code Form GUEST User Submission *
	 *****************************************/

	var submitGuestCodePaymentForm = function(paymentProcessorToken, applePayCompletion) {
		var data = {
			code: document.querySelector('input[name=code]').value,
			codeType: 'promo',
			paymentProcessorToken: paymentProcessorToken
		};
		var redirectUrl = window.location.origin + window.location.pathname + '?success=true';
		var gaTracking = {
			event: 'promo code redemption',
			properties: {
				label: null,
				category: 'code redemption',
				paymentType: applePayCompletion ? 'apple pay' : 'credit card'
			}
		};

		addPaymentAndSaveSubscription(data, redirectUrl, gaTracking, applePayCompletion, function(err) {
			if (err) promoCodeErrors(err, 'promo');
			toggleSubmitButton(false, 'Submit');
			enableForm('#code-redemption-payment-form', cardElement);

			if (applePayCompletion) applePayCompletion('fail');
		});
	};

	if (document.getElementById('code-redemption-payment-form')) {
		document.getElementById('code-redemption-payment-form').addEventListener('submit', function(e) {
			e.preventDefault();

			validationBeforeSubmit('#code-redemption-payment-form');

			var formComplete = checkIfFormComplete('guest-code-redemption');

			processPayment().then(
				function(paymentProcessorToken) {
					if (paymentProcessorToken && formComplete) {
						submitGuestCodePaymentForm(paymentProcessorToken);
					} else {
						toggleSubmitButton(false, submitButtonText);
						enableForm('#code-redemption-payment-form', cardElement);
					}
				}, function(err) {
					formatErrors(err);
					toggleSubmitButton(false, 'Submit');
					enableForm('#code-redemption-payment-form', cardElement);
				}
			);
		});
	}

	if (document.querySelector('#apple-pay-promo .apple-pay-button')) {
		buildApplePaySession(submitGuestCodePaymentForm);

		document.querySelector('#apple-pay-promo .apple-pay-button').addEventListener('click', function() {
			validationBeforeSubmit('#code-redemption-payment-form');

			var formComplete = checkIfFormComplete('guest-code-redemption', true);

			if (!formComplete) {
				enableForm('#code-redemption-payment-form');
				toggleSubmitButton(false, 'Submit');
				return;
			}

			applePayRequest.show();

			applePayRequest.on('cancel', function() {
				enableForm('#code-redemption-payment-form');
				toggleSubmitButton(false, 'Submit');
			});
		});
	}

	/********************************
	 * Promo & Gift Code Redemption *
	 ********************************/

	if (document.getElementById('code-redemption-code-form')) {
		document.getElementById('code-redemption-code-form').addEventListener('submit', function(e) {
			e.preventDefault();

			var codeType = window.location.pathname.includes('promo') ? 'promo' : 'gift';
			var options = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					codeType: codeType,
					code: document.querySelector('input[name=code]').value
				})
			};

			if (!checkIfFormComplete('code-redemption')) return;

			// Disable button while form is being submitted
			toggleSubmitButton(true, paymentProcessingText);
			removeError('general');
			disableForm('#code-redemption-code-form', cardElement);

			fetch('/proxy/v1/codeSubmission', options)
				.then(function(response) {
					if (response.ok) {
						var eventProperties = {
							label: null,
							category: 'code redemption'
						};
						var safetyTimeout = 8000;
						var redirectUrl = window.location.origin + window.location.pathname + '?success=true';

						// Safety net timeout, in case analytics fails
						var timeoutID = setTimeout(function() {
							window.location = redirectUrl;
						}, safetyTimeout);

						track('successful submission', eventProperties, function() {
							clearTimeout(timeoutID);
							window.location = redirectUrl;
						});
					} else {
						response.json().then(function(responseJSON) {
							promoCodeErrors(responseJSON, codeType);
							toggleSubmitButton(false, 'Submit');
							enableForm('#code-redemption-code-form', cardElement);
						});
					}
				})
				.catch(function() {
					toggleSubmitButton(false, 'Submit');
					enableForm('#code-redemption-code-form', cardElement);
				});
		});
	}

	/****************************
	 * Gift Purchase Submission *
	 ****************************/

	var purchaseGift = function(paymentProcessorToken, paymentSourceId) {
		var isScheduled = document.querySelector('input[name="schedule"]:checked').value === 'scheduled';
		var data = {
			amount: parseInt(document.querySelector('input[name="amount"]:checked').value, 10),
			buyerEmail: paymentSourceId ? document.querySelector('input[name="buyerEmail"]').value : document.getElementById('register-form__email').value,
			buyerFirstName: paymentSourceId ? document.querySelector('input[name="buyerFirstName"]').value : userInfo.firstName,
			buyerLastName: paymentSourceId ? document.querySelector('input[name="buyerLastName"]').value : userInfo.lastName,
			recipientEmail: document.querySelector('input[name="recipientEmail"]').value,
			recipientName: document.querySelector('input[name="recipientName"]').value,
			giftNote: document.getElementById('gift-purchase__input-message').value,
			paymentSourceId: paymentSourceId,
			paymentProcessorToken: paymentProcessorToken
		};

		if (isScheduled) data.sendDate = dayjs(document.getElementById('datepicker').value).toISOString();

		var options = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		};

		fetch('/proxy/v1/purchaseGift', options)
			.then(function(response) {
				if (response.ok) {
					var el = document.querySelector('.gift-purchase__page:not(.hide)');
					var offset = {
						top: el.getBoundingClientRect().top + window.scrollY
					};

					toggleSubmitButton(false, submitButtonText);
					enableForm('#gift-form', cardElement);

					document.querySelector('.gift-purchase__page[data-step="2"]').classList.toggle('hide');
					document.querySelector('.gift-purchase__page[data-step="3"]').classList.toggle('hide');

					// Animate to element
					window.scroll({
						top: offset.top,
						behavior: 'smooth'
					});
				} else {
					response.json().then(function(responseJSON) {
						if (responseJSON && typeof responseJSON === 'object') {
							if (Array.isArray(responseJSON)) {
								responseJSON.forEach(function(error) {
									if (error.param === 'amount') {
										addError();
									} else {
										addError(error.param, error.msg);
									}
								});
							} else {
								addError(getNested(responseJSON, 'error', 'param'), getNested(responseJSON, 'error', 'msg'));
							}
						} else {
							addError();
						}

						toggleSubmitButton(false, submitButtonText);
						enableForm('#gift-form', cardElement);
					});
				}
			})
			.catch(function() {
				addError();
				toggleSubmitButton(false, submitButtonText);
				enableForm('#gift-form', cardElement);
			});
	};

	var validateGiftPurchase = function() {
		var paymentType = document.getElementById('gift-purchase__payment-type')
			? document.getElementById('gift-purchase__payment-type').value
			: null;
		var paymentSourceId = (paymentType === 'other') ? null : paymentType;

		// Disable the submit button to prevent repeated clicks
		// Remove any generic form errors that might exist
		document.querySelector('.form__generic-errors').classList.add('hide');
		document.querySelector('.form__generic-errors').innerHTML = '';
		toggleSubmitButton(true, paymentProcessingText);
		removeError('general');
		removeError('apple-pay');
		disableForm('#gift-form', cardElement);

		if (paymentSourceId) {
			purchaseGift(null, paymentSourceId);
		} else {
			var formComplete = checkIfFormComplete('gift');

			processPayment().then(function(paymentProcessorToken) {
				if (paymentProcessorToken && formComplete) {
					purchaseGift(paymentProcessorToken);
				} else {
					toggleSubmitButton(false, submitButtonText);
					enableForm('#gift-form', cardElement);
				}
			}, function(err) {
				formatErrors(err);
				toggleSubmitButton(false, 'Submit');
				enableForm('#gift-form', cardElement);
			});
		}
	};

	if (document.getElementById('gift-form')) {
		document.getElementById('gift-form').addEventListener('submit', function(e) {
			e.preventDefault();
			validateGiftPurchase();
		});
	}

	if (document.querySelector('.gift-purchase__purchase-btn')) {
		document.querySelector('.gift-purchase__purchase-btn').addEventListener('click', function(e) {
			e.preventDefault();
			validateGiftPurchase();
		});
	}

	/********************************
	 * Registration Form Submission *
	 ********************************/
	var promoApplyButton = document.querySelector('.btn--apply-promo');
	var promoApplyErr = 'Please click "Apply" above to apply your promo code before submitting.';
	var submitRegistrationPaymentForm = function(paymentProcessorToken, applePayCompletion) {
		var planId = document.querySelector('[name="plan-id"]').type === 'radio' ? document.querySelector('[name="plan-id"]:checked').value : document.getElementById('plan-id').value;
		var programId = document.getElementById('programId').value;
		var flow = document.getElementById('flow').value;
		var promo = document.getElementById('register-form__promo').value;
		var querystring = new URLSearchParams(window.location.search);
		var data = {
			code: promo,
			flow: flow,
			paymentProcessorToken: paymentProcessorToken,
			programId: programId,
			sso: querystring.get('sso'),
			sig: querystring.get('sig')
		};
		var redirect = null;
		var gaTracking = {
			event: 'successful submission',
			properties: {
				label: promo,
				category: 'sign up',
				paymentType: applePayCompletion ? 'apple pay' : 'credit card'
			}
		};

		if (planId) data.planId = planId;

		// If a promo code was added but not applied
		if (promo && promoApplyButton && promoApplyButton.innerText == 'Apply') {
			addError('general', promoApplyErr);
			toggleSubmitButton(!!applePayCompletion, submitButtonText);
			enableForm('.register-form', cardElement);

			if (onboardingFlow) {
				// Refresh the carousel after the error has been added to allow for scrolling
				window.onboardingCarousel.trigger('refresh.owl.carousel');
			}

			return;
		}

		addPaymentAndSaveSubscription(data, redirect, gaTracking, applePayCompletion, function(err) {
			if (err && typeof err === 'object') {
				if (Array.isArray(err)) {
					err.forEach(function(error) {
						if (error.param === 'User already exists') {
							addError('email', 'This email address already has a Glo account. Please log in to continue.');
						} else {
							addError(error.param, error.msg);
						}
					});
				} else {
					addError(getNested(err, 'error', 'param'), getNested(err, 'error', 'msg'));
				}
			} else {
				addError();
			}

			if (onboardingFlow) {
				// Refresh the carousel after the error has been added to allow for scrolling
				window.onboardingCarousel.trigger('refresh.owl.carousel');
			}

			toggleSubmitButton(!!applePayCompletion, submitButtonText);
			enableForm('.register-form', cardElement);

			if (applePayCompletion) applePayCompletion('fail');
		});
	};

	if (document.getElementById('register-form')) {
		document.getElementById('register-form').addEventListener('submit', function(e) {
			e.preventDefault();
			validationBeforeSubmit('.register-form');

			var formComplete = checkIfFormComplete('register');

			processPayment().then(
				function(paymentProcessorToken) {
					if (paymentProcessorToken && formComplete) {
						submitRegistrationPaymentForm(paymentProcessorToken);
					} else {
						toggleSubmitButton(false, submitButtonText);
						enableForm('.register-form', cardElement);
					}
				},
				function(err) {
					// Handle errors from server
					formatErrors(err);
					toggleSubmitButton(false, submitButtonText);
					enableForm('.register-form', cardElement);
				}
			);
		});
	}

	if (document.querySelector('#apple-pay-register .apple-pay-button')) {
		buildApplePaySession(submitRegistrationPaymentForm, 'register');

		document.querySelector('#apple-pay-register .apple-pay-button').addEventListener('click', function() {
			validationBeforeSubmit('.register-form');

			var formComplete = checkIfFormComplete('register', true);

			if (!formComplete) {
				toggleSubmitButton(false, submitButtonText);
				enableForm('.register-form', cardElement);
				return;
			}

			var deltaEmailArray = [
				'delta.com',
				'deltaprivatejets.com',
				'mltvacations.com',
				'deltacommunitycu.com',
				'endeavorair.com',
				'monroe-energy.com'
			];
			var amazonEmail = 'amazon.com';
			var emailAddress = userInfo.email;
			var emailDomain = emailAddress ? emailAddress.split('@')[1] : null;
			var isDeltaEmail = deltaEmailArray.includes(emailDomain);
			var isAmazonEmail = amazonEmail.includes(emailDomain);
			var planId = document.querySelector('[name="plan-id"]').type === 'radio'
				? document.querySelector('[name="plan-id"]:checked').value
				: document.getElementById('plan-id').value;
			var isYogaAlliance = planId === '12_month_ya';
			var subscriptionAmount = 3000; // Fallback amount
			var subscriptionPeriod = planId
				? planId.includes('12_month')
					? 'yearly'
					: 'monthly'
				: document.getElementById('subscription-period')
					? document.getElementById('subscription-period').value
					: 'monthly';
			var labelCopy = isYogaAlliance
				? `Recurring ${subscriptionPeriod} subscription fee.`
				: `Recurring ${subscriptionPeriod} subscription fee, billed after trial period.`;

			if (planId) {
				subscriptionAmount = document.querySelector('[for="' + document.querySelector('[name="plan-id"]:checked').id + '"] .register-form__plan-title').dataset.price;

				subscriptionAmount = parseInt(subscriptionAmount) * 100;
			} else if (document.getElementById('billing-total')) {
				subscriptionAmount = document.getElementById('billing-total').innerText.replace(/\D/g, '');
				subscriptionAmount = parseInt(subscriptionAmount);
			}

			if (isDeltaEmail || isAmazonEmail) subscriptionAmount = 999;
			if (isYogaAlliance) subscriptionAmount = 15000;

			var paymentOptions = {
				total: {
					label: 'Glo',
					amount: subscriptionAmount
				},
				displayItems: [{
					label: labelCopy,
					amount: subscriptionAmount
				}]
			};

			applePayRequest.update(paymentOptions);
			applePayRequest.show();

			applePayRequest.on('cancel', function() {
				enableForm('.register-form');
				toggleSubmitButton(false, submitButtonText);
			});
		});
	}

	let applyPromoCode = () => {
		throw new Error('no promo field found');
	};
	if (promoApplyButton) {
		var promoInput = document.querySelector('input[name="promo"]');

		applyPromoCode = function(monthlyplanClicked) {
			if (promoInput.disabled) {
				promoApplyButton.setAttribute('disabled', true);

				// Reset the subscription plans if promo was removed
				fetch('/getDefaultSubscriptionPlans', { method: 'GET' })
					.then(function(response) {
						if (!response.ok) throw Error('Issue fetching default subscriptions');
						return response;
					})
					.then(response => response.json())
					.then(response => {
						promoApplyButton.innerText = 'Apply';
						promoApplyButton.removeAttribute('disabled');
						promoInput.removeAttribute('disabled');
						promoInput.value = '';

						userInfo.promoCode = null;

						loadSubscriptionPlans(response.subscriptionPlans);
					})
					.catch(function() {
						promoApplyButton.removeAttribute('disabled');
					});
			} else {
				var genericErrorsEl = document.querySelector('.form__generic-errors');
				var attemptingApply = true;
				validatePromoCode(attemptingApply, monthlyplanClicked);

				if (genericErrorsEl && genericErrorsEl.innerText === promoApplyErr) {
					removeError('general');
				}
			}
		};

		promoInput.addEventListener('blur', function(e) {
			var userClickedSubmit = e.relatedTarget && !e.relatedTarget.classList.contains('register-form__submit');

			if (promoInput.value && !e.relatedTarget || userClickedSubmit) {
				applyPromoCode();
			}
		});

		promoApplyButton.addEventListener('click', applyPromoCode);

		promoApplyButton.addEventListener('mouseover', function() {
			promoApplyButton.innerText = promoInput.disabled ? 'Remove' : 'Apply';
		});

		promoApplyButton.addEventListener('mouseout', function() {
			promoApplyButton.innerText = promoInput.disabled ? 'Applied!' : 'Apply';
		});
	}

	// Set the promo code to WELLBEING by default


	/********************************
	 * Nudge to Annual Plan Modal (START)
	 ********************************/
	const nudgeModalAnnualButton = document.querySelector('#nudge-to-annual-plan-modal #annual-btn');
	const nudgeModalMonthlyButton = document.querySelector('#nudge-to-annual-plan-modal #monthly-btn');
	const nudgeModalCloseButton = document.querySelector('#nudge-to-annual-plan-modal .close-btn');
	const monthlyPlanRadio = document.querySelector('[id*="1_month"]');

	const toggleNudgeModal = () => {
		const modalWrapper = document.querySelector('.modal__wrapper');
		const modal = document.querySelector('#nudge-to-annual-plan-modal');
		const isHidden = modalWrapper.classList.contains('hide');

		if (isHidden) {
			modalWrapper.classList.remove('hide');
			modal.setAttribute('open', true);
			document.body.classList.add('no-scroll');
		} else {
			modalWrapper.classList.add('hide');
			modal.setAttribute('open', false);
			document.body.classList.remove('no-scroll');
		}
	};

	const togglePromoField = () => {
		const promoContainer = document.querySelector('.register-form__promo-container');

		if (promoContainer.classList.contains('hide')) promoContainer.classList.remove('hide');

		document.querySelector('.register-form__show-promo').classList.add('hide');
	};

	const nudgeToAnualPlan = () => {
		const promoField = document.getElementById('register-form__promo');
		const annualPlanRadio = document.querySelector('[id*="12_month"]');

		// Hide modal
		toggleNudgeModal();

		// Select annual plan radio
		annualPlanRadio.checked = true;

		// Show promo field
		togglePromoField();

		// Set and apply promo code
		promoField.value = 'WELLBEING';
		applyPromoCode();
	};

	if (nudgeModalMonthlyButton) nudgeModalMonthlyButton.addEventListener('click', toggleNudgeModal);
	if (nudgeModalAnnualButton) nudgeModalAnnualButton.addEventListener('click', nudgeToAnualPlan);
	if (monthlyPlanRadio) monthlyPlanRadio.addEventListener('click', toggleNudgeModal);
	if (nudgeModalCloseButton) nudgeModalCloseButton.addEventListener('click', toggleNudgeModal);
	/********************************
	 * Nudge to Annual Plan Modal (END)
	 ********************************/

	// Promo codes on registration
	if (document.querySelector('.register-form__show-promo')) {
		document.querySelector('.register-form__show-promo').addEventListener('click', togglePromoField);
	}
});
